import type { FocusableElement } from '@react-types/shared';

/* eslint-disable react/jsx-props-no-spreading */
import { ButtonLink, Confirm } from '@coop/components';
import { type ReactNode, useRef } from 'react';

import { useIsUserAuthenticated, useIsUserCompany } from '../../hooks/useUserProfile';
import { ga4B2BTracking } from '../../tracking/ga4/b2b';
import { ga4UserTracking } from '../../tracking/ga4/user';
import { isInB2BCrossContext, useIsB2BRoute } from '../../utils/b2bUtils';
import { getLoginUrlWithReturnUrl } from '../../utils/urlUtils';

/**
 * The notifications that are shown when the user is logged in as a private user on the company site or as a company user on the private site.
 * The add to cart action is blocked when these notifications are shown.
 */
export const B2BCrossContextNotifications = (props: {
    show: boolean;
    close: () => void;
    renderTrigger?: (
        ref: React.RefObject<HTMLButtonElement | null>,
        isBlocked: boolean,
    ) => ReactNode;
    continueAsPrivateUrl: string;
    continueAsCompanyUrl?: string;
}) => {
    const isB2BRoute = useIsB2BRoute();
    const buyButtonRef = useRef<HTMLButtonElement>(null);
    const { privateUserOnCompanySite, companyUserOnPrivateSite } = isInB2BCrossContext({
        isB2BRoute,
        isUserCompany: useIsUserCompany(),
        isAuthenticated: useIsUserAuthenticated(),
    });

    if (props.renderTrigger && !companyUserOnPrivateSite && !privateUserOnCompanySite) {
        return <>{props.renderTrigger(buyButtonRef, false)}</>;
    }

    const clickInteractionTracking: React.MouseEventHandler<FocusableElement> = (e) => {
        ga4B2BTracking.b2bCrossContextPopupInteraction((e.currentTarget as HTMLElement).innerText);
    };

    const clickLoginTracking = () => {
        ga4UserTracking.login(null, 'start', 'enter zip code cart');
    };

    return (
        <>
            {privateUserOnCompanySite && (
                <Confirm
                    show={props.show}
                    close={props.close}
                    renderTrigger={(ref) => props.renderTrigger?.(ref, true)}
                    heading="Vill du handla till dig själv eller till jobbet?"
                    message="Du är inloggad som privatperson men handlar just nu från företagssidorna."
                    renderConfirmTrigger={(bProps) => (
                        <ButtonLink
                            {...bProps}
                            href={getLoginUrlWithReturnUrl('company', 'login')}
                            onClick={(e) => {
                                clickInteractionTracking(e);
                                clickLoginTracking();
                            }}
                        >
                            Logga in som Företagskund
                        </ButtonLink>
                    )}
                    renderCancelTrigger={(bProps) => (
                        <ButtonLink
                            {...bProps}
                            href={props.continueAsPrivateUrl}
                            onClick={clickInteractionTracking}
                        >
                            Fortsätt som Privat
                        </ButtonLink>
                    )}
                    disableOutsideClick
                />
            )}
            {companyUserOnPrivateSite && (
                <Confirm
                    show={props.show}
                    close={props.close}
                    renderTrigger={(ref) => props.renderTrigger?.(ref, true)}
                    heading="Vill du handla till dig själv eller till jobbet?"
                    message="Du är inloggad med ditt företagskonto men handlar just nu från privatsidorna."
                    renderConfirmTrigger={(bProps) => (
                        <ButtonLink
                            {...bProps}
                            href={getLoginUrlWithReturnUrl('private', 'login')}
                            onClick={(e) => {
                                clickInteractionTracking(e);
                                clickLoginTracking();
                            }}
                        >
                            Logga in som Privatkund
                        </ButtonLink>
                    )}
                    renderCancelTrigger={(bProps) => (
                        <ButtonLink
                            {...bProps}
                            href={props.continueAsCompanyUrl}
                            onClick={clickInteractionTracking}
                        >
                            Fortsätt som Företag
                        </ButtonLink>
                    )}
                    disableOutsideClick
                />
            )}
        </>
    );
};

export default B2BCrossContextNotifications;
